// Detects when a newer deploy is live vs the version currently loaded in the
// browser. Polls /version.json on mount, every 10 minutes, on window focus and
// when the page becomes visible again (iOS home-screen PWAs resume from the
// background without a reliable `focus`, and background timers are paused —
// `visibilitychange` is the resume signal that actually fires); returns an object
// with { updateAvailable, latestVersion, dismiss, silentReloading, updateRequired,
// requiredCard }.
// Drives the UpdateAvailableBanner modal.
//
// Why this exists:
//   iOS PWAs cache the JS bundle aggressively. Without an in-app cue, users on
//   the home-screen PWA can keep running months-old code without realizing it.
//
// KAN-886: the app now updates itself QUIETLY at a safe moment (see
// useSilentAppUpdate — wake-up from the background, or a tab switch, when the
// silent-update gate says nothing is at risk). The "Update now / Later" prompt
// is only the BACKSTOP: it shows when a newer version has been pending >= 24h
// or when the silent reload did not take (loop guard), and "Later" still defers
// it for the session.

import { useState, useEffect, useCallback, useRef } from 'react';
import { withTimeout } from '../utils/async/withTimeout';
import { unregisterAndClear } from '../serviceWorkerRegistration';
import { useSilentAppUpdate, PROMPT_BACKSTOP_MS } from './useSilentAppUpdate';
import { useRequiredUpdateFlow } from './useRequiredUpdateFlow';
import { APP_COMPAT_LEVEL } from '../config/appCompat';
import { markUpdateReload } from '../utils/app/updateReloadMarker';

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
// A version check that never settles (iOS can drop the socket while the PWA is
// suspended) would leave `inFlight` stuck and silence every later check — bound it.
const CHECK_TIMEOUT_MS = 8000;
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'dev';
const SESSION_KEY = 'crewvex.update.dismissedVersion';

export function dismissCurrentUpdate(version) {
  try {
    sessionStorage.setItem(SESSION_KEY, version);
  } catch (_) {
    // sessionStorage unavailable (private-mode quirks) — silently skip.
  }
}

/**
 * Clears the session-dismiss flag. Called by the Banner's "Update now" path
 * after a successful update so any stale dismiss entry can't suppress the
 * next version-check after reload.
 */
export function clearDismissedUpdate() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (_) {}
}

/**
 * Reload onto the freshest deployed bundle.
 *
 * Always-fresh strategy (2026-07-13): CrewVex dropped the offline service worker
 * (see src/serviceWorkerRegistration.js), so a plain reload already fetches the
 * current index.html + hashed bundle from the network. We first run a BEST-EFFORT,
 * TIME-BOUNDED teardown of any worker + caches a TRANSITIONAL install may still
 * carry (an install not yet healed by the self-destruct worker) so its reload
 * isn't intercepted by a stale precache — then reload with a cache-buster.
 *
 * The reload is UNCONDITIONAL and the teardown can NEVER hang it (the prior P0:
 * on iOS PWA the serviceWorker/cache promises can stay unsettled forever — no
 * resolve, no reject, so try/catch never fires — and the splash raised at tap
 * would freeze with no exit). A watchdog armed BEFORE the await guarantees the
 * reload fires; the `navigated` latch keeps it idempotent.
 */
const TEARDOWN_TIMEOUT_MS = 2000;
const NAVIGATE_WATCHDOG_MS = 3500;

export async function performAppUpdate() {
  let navigated = false;
  const navigate = () => {
    if (navigated) return;
    navigated = true;
    clearDismissedUpdate();
    markUpdateReload();
    // Cache-buster URL — forces the browser to round-trip to the server for
    // index.html, which references the new hashed bundle paths.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_v', String(Date.now()));
      window.location.replace(url.toString());
    } catch (_) {
      // Last-resort fallback if URL APIs unavailable.
      window.location.reload();
    }
  };

  // Absolute tap→reload ceiling, armed BEFORE any await so a hung teardown can
  // never strand the user on the splash. The `navigated` latch makes it idempotent.
  const watchdog = setTimeout(navigate, NAVIGATE_WATCHDOG_MS);

  try {
    await withTimeout(unregisterAndClear(), TEARDOWN_TIMEOUT_MS);
  } catch (_) {
    /* best-effort — the watchdog / trailing navigate() still reload */
  }

  clearTimeout(watchdog);
  navigate();
}

/**
 * Cosmetic boot cleanup: removes the `_v` cache-buster performAppUpdate() puts
 * in the address bar. By the time any code runs, the param has already done its
 * job (the browser round-tripped for a fresh index.html), so stripping it is
 * display-only — same replaceState pattern as usePreFillFromAi. Best-effort:
 * on any failure the URL just keeps the param; this must never affect boot.
 */
export function stripUpdateCacheBusterFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_v')) return;
    url.searchParams.delete('_v');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (_) {
    // Display-only cleanup — never let it interfere with boot.
  }
}

function getDismissedVersion() {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch (_) {
    return null;
  }
}

export function useUpdateAvailable() {
  const [latestVersion, setLatestVersion] = useState(null);
  // Bumped after each check / dismiss so the 24h backstop re-evaluates.
  const [, setTick] = useState(0);
  const pendingRef = useRef({ version: null, since: 0 });
  // KAN-887: version.json's `compat` ahead of this bundle's level → this copy is
  // too old to keep running. Latched for the page's life; anything missing or
  // not a higher integer is ignored (a rollback must never lock people out).
  const [updateRequired, setUpdateRequired] = useState(false);

  useEffect(() => {
    // Local dev / unset version → no-op (we'd false-positive on every hot reload).
    if (BUILD_VERSION === 'dev') return undefined;

    let cancelled = false;
    // focus + visibilitychange usually fire together on resume — one request.
    let inFlight = false;

    const check = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        // cache:'no-store' bypasses HTTP cache so we always read the deployed file,
        // not a stale-cached version (the whole point of this check).
        const res = await withTimeout(fetch('/version.json', { cache: 'no-store' }), CHECK_TIMEOUT_MS);
        if (cancelled || !res.ok) return;
        const data = await withTimeout(res.json(), CHECK_TIMEOUT_MS);
        if (Number.isInteger(data?.compat) && data.compat > APP_COMPAT_LEVEL) {
          setUpdateRequired(true);
        }
        if (data?.version && data.version !== BUILD_VERSION) {
          if (pendingRef.current.version !== data.version) {
            pendingRef.current = { version: data.version, since: Date.now() };
          }
          setLatestVersion(data.version);
        }
      } catch (_) {
        // Network error / offline — silent. Try again on next interval.
      } finally {
        inFlight = false;
        if (!cancelled) setTick((t) => t + 1);
      }
    };

    // Initial check + recurring poll + on resume (focus, or the page becoming
    // visible again — the signal iOS PWAs reliably send when reopened).
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const { silentReloading, guardTripped, reloadQuietlyNow } = useSilentAppUpdate({
    latestVersion,
    performUpdate: performAppUpdate,
  });

  // "Later" is session-scoped and only hides the PROMPT; the silent paths ignore it.
  const dismiss = useCallback(() => {
    if (latestVersion) dismissCurrentUpdate(latestVersion);
    setTick((t) => t + 1);
  }, [latestVersion]);

  const dismissed = !!latestVersion && getDismissedVersion() === latestVersion;
  const pendingLongEnough =
    !!latestVersion && Date.now() - pendingRef.current.since >= PROMPT_BACKSTOP_MS;
  const updateAvailable =
    !updateRequired && !!latestVersion && !dismissed && (pendingLongEnough || guardTripped);

  const requiredCard = useRequiredUpdateFlow({ updateRequired, reloadQuietlyNow, silentReloading });

  return { updateAvailable, latestVersion, dismiss, silentReloading, updateRequired, requiredCard };
}
